<template>
  <v-dialog
    :value="true"
    max-width="1792px"
    scrollable
    persistent
  >
    <v-card
      id="ab-reports"
      :loading="loading"
    >
      <v-card-title>
        <span>Ab Test Report </span>

        <v-spacer />

        <v-btn
          icon
          @click="handleClose"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <div
        v-if="loading"
        class="d-flex flex-column align-center py-4 px-4"
      >
        <h3 class="mb-6 mt-2">
          Loading Report Data...
        </h3>
        <v-progress-circular
          :size="50"
          color="primary"
          indeterminate
        />
      </div>
      <template v-else>
        <v-card-text>
          <v-container justify="space-around">
            <!-- Filter Form -->
            <FilterForm
              class="mb-5"
              :ab-buckets="abBuckets"
              :ab-tests="abTests"
              :initial-filters.sync="filters"
              @apply-filters="handleApplyFilters"
              @clear-filters="handleClearFilters"
            />

            <!-- Summary Card -->
            <ReportSummary
              class="mb-5"
              :summary-data="summary"
            />

            <!-- Report Table -->
            <ReportTable
              class="mb-5"
              :report-data="reportData"
            />
          </v-container>
        </v-card-text>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex';
import { formatPercentage } from '@/filters';
import { required } from 'vuelidate/lib/validators';
import FilterForm from './FilterForm.vue';
import ReportSummary from './ReportSummary.vue';
import ReportTable from './ReportTable.vue';

export default {
  name: 'AbTestReport',

  components: {
    FilterForm,
    ReportSummary,
    ReportTable,
  },

  emits: ['close'],

  data() {
    return {
      loading: true,
      filters: {
        abBucketId: null,
        abTestId: null,
        dateFrom: '',
        dateTo: '',
      },

      summary: {
        matchedLeadCount: 0,
        acceptedLeadCount: 0,
        fundedLeadCount: 0,
        totalFundedAmount: 0,
        matchedLeadPercentage: 0,
        acceptedLeadPercentage: 0,
        fundedLeadPercentage: 0,
        acceptanceRate: 0,
        fundingRate: 0,
      },
      reportData: [],

      abBuckets: [],
      abTests: [],
    };
  },

  mounted() {
    this.fetchReportData();
  },

  methods: {
    formatPercentage,
    ...mapActions('notification', ['setNotification']),

    handleClose() {
      this.$emit('close');
    },

    async fetchReportData() {
      try {
        this.loading = true;

        const { data } = await this.$axios.get('api/v1/ab-tests/reports', {
          params: {
            grouped: 1,
            abBucketId: this.filters.abBucketId,
            abTestId: this.filters.abTestId,
            dateFrom: this.filters.dateFrom,
            dateTo: this.filters.dateTo,
          },
        });

        // Update data with API response
        this.reportData = data.reportData || [];
        this.summary = { ...this.summary, ...data.summary || {} };
        this.abBuckets = data.abBuckets || [];
        this.abTests = data.abTests || [];
      } catch (error) {
        this.setNotification({
          text: 'Failed to load report data. Please try again.',
          type: 'error',
        });
        // eslint-disable-next-line no-console
        console.error('Error fetching report data:', error);
      } finally {
        this.loading = false;
      }
    },

    handleClearFilters() {
      this.filters = {
        abBucketId: null,
        abTestId: null,
        dateFrom: '',
        dateTo: '',
      };

      // Fetch data with cleared filters
      this.fetchReportData();

      this.setNotification({
        text: 'Filters cleared successfully',
        type: 'info',
      });
    },

    async handleApplyFilters(filters) {
      console.log('Applying filters:', filters);
      this.filters = { ...filters };

      // Fetch new data with applied filters
      await this.fetchReportData();

      this.setNotification({
        text: 'Filters applied successfully',
        type: 'success',
      });
    },
  },

  validations: {
    bucketData: {
      bucket_name: { required },
      description: { required },
      bucket_type: { required },
      is_active: { required },
      default_ratio: { required },
      testing_ratio: { required },
    },
  },
};
</script>

<style>
#ab-reports .v-toolbar__title,
#ab-reports .v-btn__content {
  text-transform: none !important;
}

#ab-reports .v-toolbar__content {
  padding-left: 0;
  padding-right: 0;
}
</style>
